/**
 * Lender Portal - Dashboard
 * Main dashboard for finance providers to manage their profile and leads
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useToast } from '../../hooks/use-toast';
import {
  Banknote,
  Building2,
  Users,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Clock,
  Mail,
  Phone,
  Car,
  DollarSign,
  Settings,
  LogOut,
  Loader2,
  AlertTriangle,
  Eye,
  MessageSquare,
  RefreshCw,
  Shield,
  BarChart3,
  FileText
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LenderDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  const [lenderInfo, setLenderInfo] = useState(null);
  const [provider, setProvider] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    description: '',
    website_url: '',
    accepts_new_vehicles: true,
    accepts_used_vehicles: true,
    max_vehicle_age: 15,
    min_loan_amount: 500000,
    max_loan_amount: 20000000,
    min_tenure_months: 12,
    max_tenure_months: 84,
    interest_rate_min: 8,
    interest_rate_max: 15,
    interest_level: 'medium',
    min_deposit_percent: 10,
    approval_time: '1-3 days',
    online_application: true,
    documents_required: 'standard',
    income_types_accepted: ['salaried'],
    early_repayment_penalty: false
  });

  // Check auth and fetch data
  useEffect(() => {
    const token = localStorage.getItem('lender_token');
    const info = localStorage.getItem('lender_info');
    
    if (!token) {
      navigate('/lender/login');
      return;
    }
    
    if (info) {
      setLenderInfo(JSON.parse(info));
    }
    
    fetchDashboardData(token);
  }, [navigate]);

  const fetchDashboardData = async (token) => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${API_URL}/api/finance/lender/dashboard`, {
        params: { lender_token: token }
      });
      
      if (response.data.success) {
        setLenderInfo(response.data.lender);
        setProvider(response.data.provider);
        setLeads(response.data.leads || []);
        setStats(response.data.stats || {});
        
        // Initialize profile form with provider data
        if (response.data.provider) {
          setProfileForm({
            description: response.data.provider.description || '',
            website_url: response.data.provider.website_url || '',
            accepts_new_vehicles: response.data.provider.accepts_new_vehicles ?? true,
            accepts_used_vehicles: response.data.provider.accepts_used_vehicles ?? true,
            max_vehicle_age: response.data.provider.max_vehicle_age || 15,
            min_loan_amount: response.data.provider.min_loan_amount || 500000,
            max_loan_amount: response.data.provider.max_loan_amount || 20000000,
            min_tenure_months: response.data.provider.min_tenure_months || 12,
            max_tenure_months: response.data.provider.max_tenure_months || 84,
            interest_rate_min: response.data.provider.interest_rate_min || 8,
            interest_rate_max: response.data.provider.interest_rate_max || 15,
            interest_level: response.data.provider.interest_level || 'medium',
            min_deposit_percent: response.data.provider.min_deposit_percent || 10,
            approval_time: response.data.provider.approval_time || '1-3 days',
            online_application: response.data.provider.online_application ?? true,
            documents_required: response.data.provider.documents_required || 'standard',
            income_types_accepted: response.data.provider.income_types_accepted || ['salaried'],
            early_repayment_penalty: response.data.provider.early_repayment_penalty ?? false
          });
        }
      }
      
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('lender_token');
        localStorage.removeItem('lender_info');
        navigate('/lender/login');
      }
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('lender_token');
    localStorage.removeItem('lender_info');
    navigate('/lender/login');
  };

  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    const token = localStorage.getItem('lender_token');
    
    try {
      setUpdatingStatus(true);
      
      await axios.patch(
        `${API_URL}/api/finance/lender/leads/${leadId}/status`,
        newStatus,
        { params: { lender_token: token } }
      );
      
      toast({
        title: "Status Updated",
        description: `Lead status changed to ${newStatus}`
      });
      
      // Refresh data
      fetchDashboardData(token);
      setShowLeadDialog(false);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update lead status",
        variant: "destructive"
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSaveProfile = async () => {
    const token = localStorage.getItem('lender_token');
    
    try {
      setSavingProfile(true);
      
      await axios.put(
        `${API_URL}/api/finance/lender/provider`,
        profileForm,
        { params: { lender_token: token } }
      );
      
      toast({
        title: "Profile Saved",
        description: "Your provider profile has been updated"
      });
      
      fetchDashboardData(token);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save profile",
        variant: "destructive"
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'disbursed': return 'bg-blue-100 text-blue-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'in_review': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="lender-dashboard">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
              <Banknote className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">{lenderInfo?.company_name || 'Lender Portal'}</h1>
              <p className="text-xs text-gray-500">Finance Partner Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {lenderInfo?.status === 'pending_review' && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                <Clock className="h-3 w-3 mr-1" />
                Pending Approval
              </Badge>
            )}
            {provider?.is_verified && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <Shield className="h-3 w-3 mr-1" />
                Verified
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Status Alert */}
        {lenderInfo?.status === 'pending_review' && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              Your account is pending approval. You&apos;ll receive an email once verified. 
              In the meantime, you can complete your profile.
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-2xl font-bold">{stats.total_leads || 0}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.approved || 0}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Disbursed</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.disbursed || 0}</p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approval Rate</p>
                  <p className="text-2xl font-bold">{stats.approval_rate || 0}%</p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="leads" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Leads
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Provider Profile
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card>
              <CardHeader>
                <CardTitle>Finance Leads</CardTitle>
                <CardDescription>
                  Manage customer enquiries and track application status
                </CardDescription>
              </CardHeader>
              <CardContent>
                {leads.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map(lead => (
                        <TableRow key={lead.id}>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{lead.buyer_name || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{lead.buyer_email}</div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {lead.listing_id ? (
                              <Link 
                                to={`/listing/${lead.listing_id}`}
                                className="text-blue-600 hover:underline"
                                target="_blank"
                              >
                                View Listing
                              </Link>
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell className="font-medium">
                            {lead.vehicle_price?.toLocaleString() || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(lead.status)}>
                              {lead.status || 'submitted'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => {
                                setSelectedLead(lead);
                                setShowLeadDialog(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No leads yet</p>
                    <p className="text-sm">Customer enquiries will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Provider Profile</CardTitle>
                <CardDescription>
                  Configure your finance offerings and visibility to customers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={profileForm.description}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe your finance services..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Website URL</Label>
                    <Input
                      value={profileForm.website_url}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, website_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                {/* Loan Terms */}
                <div className="space-y-4">
                  <h4 className="font-medium">Loan Terms</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Min Loan Amount</Label>
                      <Input
                        type="number"
                        value={profileForm.min_loan_amount}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, min_loan_amount: parseFloat(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <Label>Max Loan Amount</Label>
                      <Input
                        type="number"
                        value={profileForm.max_loan_amount}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, max_loan_amount: parseFloat(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <Label>Min Interest (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={profileForm.interest_rate_min}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, interest_rate_min: parseFloat(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <Label>Max Interest (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={profileForm.interest_rate_max}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, interest_rate_max: parseFloat(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <Label>Min Deposit (%)</Label>
                      <Input
                        type="number"
                        value={profileForm.min_deposit_percent}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, min_deposit_percent: parseFloat(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <Label>Approval Time</Label>
                      <Select
                        value={profileForm.approval_time}
                        onValueChange={(v) => setProfileForm(prev => ({ ...prev, approval_time: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="instant">Instant</SelectItem>
                          <SelectItem value="<24h">Less than 24h</SelectItem>
                          <SelectItem value="1-3 days">1-3 Days</SelectItem>
                          <SelectItem value=">3 days">More than 3 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Min Tenure (months)</Label>
                      <Input
                        type="number"
                        value={profileForm.min_tenure_months}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, min_tenure_months: parseInt(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <Label>Max Tenure (months)</Label>
                      <Input
                        type="number"
                        value={profileForm.max_tenure_months}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, max_tenure_months: parseInt(e.target.value) }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-4">
                  <h4 className="font-medium">Vehicle & Applicant Criteria</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <Label>Accept New Vehicles</Label>
                      <Switch
                        checked={profileForm.accepts_new_vehicles}
                        onCheckedChange={(v) => setProfileForm(prev => ({ ...prev, accepts_new_vehicles: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <Label>Accept Used Vehicles</Label>
                      <Switch
                        checked={profileForm.accepts_used_vehicles}
                        onCheckedChange={(v) => setProfileForm(prev => ({ ...prev, accepts_used_vehicles: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <Label>Online Application</Label>
                      <Switch
                        checked={profileForm.online_application}
                        onCheckedChange={(v) => setProfileForm(prev => ({ ...prev, online_application: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <Label>Early Repayment Penalty</Label>
                      <Switch
                        checked={profileForm.early_repayment_penalty}
                        onCheckedChange={(v) => setProfileForm(prev => ({ ...prev, early_repayment_penalty: v }))}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Max Vehicle Age (years)</Label>
                    <Input
                      type="number"
                      className="w-32"
                      value={profileForm.max_vehicle_age}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, max_vehicle_age: parseInt(e.target.value) }))}
                    />
                  </div>
                  
                  <div>
                    <Label>Income Types Accepted</Label>
                    <div className="flex gap-2 mt-2">
                      {['salaried', 'self_employed', 'business'].map(type => (
                        <Button
                          key={type}
                          type="button"
                          variant={profileForm.income_types_accepted.includes(type) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            setProfileForm(prev => ({
                              ...prev,
                              income_types_accepted: prev.income_types_accepted.includes(type)
                                ? prev.income_types_accepted.filter(t => t !== type)
                                : [...prev.income_types_accepted, type]
                            }));
                          }}
                        >
                          {type.replace('_', ' ')}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingProfile ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Save Profile
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>Performance Analytics</CardTitle>
                <CardDescription>
                  Track your conversion rates and performance metrics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">Analytics Coming Soon</p>
                  <p className="text-sm">Detailed performance metrics will be available here</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Lead Detail Dialog */}
      <Dialog open={showLeadDialog} onOpenChange={setShowLeadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>
              View and update customer application
            </DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                <h4 className="font-medium text-sm text-gray-700">Customer Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium">{selectedLead.buyer_name || 'N/A'}</span>
                  <span className="text-gray-500">Email:</span>
                  <span className="font-medium">{selectedLead.buyer_email || 'N/A'}</span>
                  <span className="text-gray-500">Phone:</span>
                  <span className="font-medium">{selectedLead.buyer_phone || 'N/A'}</span>
                  <span className="text-gray-500">Income Type:</span>
                  <span className="font-medium capitalize">{selectedLead.income_type || 'N/A'}</span>
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                <h4 className="font-medium text-sm text-blue-700">Loan Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-500">Vehicle Price:</span>
                  <span className="font-medium">{selectedLead.vehicle_price?.toLocaleString() || 'N/A'}</span>
                  <span className="text-gray-500">Deposit:</span>
                  <span className="font-medium">{selectedLead.deposit_amount?.toLocaleString() || 'N/A'}</span>
                  <span className="text-gray-500">Preferred Tenure:</span>
                  <span className="font-medium">{selectedLead.preferred_tenure || 'N/A'} months</span>
                  <span className="text-gray-500">Monthly Budget:</span>
                  <span className="font-medium">{selectedLead.monthly_budget?.toLocaleString() || 'N/A'}</span>
                </div>
              </div>
              
              <div>
                <Label>Update Status</Label>
                <div className="flex gap-2 mt-2">
                  {['in_review', 'approved', 'rejected', 'disbursed'].map(status => (
                    <Button
                      key={status}
                      variant={selectedLead.status === status ? 'default' : 'outline'}
                      size="sm"
                      disabled={updatingStatus}
                      onClick={() => handleUpdateLeadStatus(selectedLead.id, status)}
                      className={selectedLead.status === status ? getStatusColor(status) : ''}
                    >
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeadDialog(false)}>
              Close
            </Button>
            {selectedLead?.buyer_email && (
              <Button onClick={() => window.location.href = `mailto:${selectedLead.buyer_email}`}>
                <Mail className="h-4 w-4 mr-2" />
                Contact Customer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LenderDashboard;
